import React, { useState } from 'react';
import {
  List,
  Show,
  Create,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  SimpleShowLayout,
  SimpleForm,
  TextInput,
  NumberInput,
  CreateButton,
  ShowButton,
  DeleteButton,
  TopToolbar,
  FunctionField,
  required,
  useDataProvider,
  useNotify,
} from 'react-admin';
import {
  Typography,
  Grid,
  Box,
  Chip,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  List as MuiList,
  ListItem,
  ListItemText,
  Checkbox,
  ListItemIcon,
} from '@mui/material';

const VestingPlanListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

const VestingPlanShowActions = () => (
  <TopToolbar>
    <DeleteButton />
  </TopToolbar>
);

const VestingPlanDetails = ({ record }: any) => {
  if (!record) return <Typography>No vesting plan data</Typography>;

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>📅 Vesting Plan Details</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Vesting Plan ID</Typography>
              <Typography variant="body1">
                <Chip label={record.id} size="small" color="primary" />
              </Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Name</Typography>
              <Typography variant="body1">{record.name}</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Description</Typography>
              <Typography variant="body1">{record.description}</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Cliff Duration</Typography>
              <Typography variant="body1">{record.cliffDuration} months</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Total Periods</Typography>
              <Typography variant="body1">{record.totalPeriods}</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">TGE Percentage</Typography>
              <Typography variant="body1">{(record.tgePercent / 100).toFixed(2)}%</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Unlock per Period</Typography>
              <Typography variant="body1">{(record.unlockPercentPerPeriod / 100).toFixed(2)}%</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Status</Typography>
              <Chip 
                label={record.active ? 'Active' : 'Inactive'} 
                color={record.active ? 'success' : 'default'} 
                size="small" 
              />
            </Box>
          </Grid>
        </Grid>
        
        {record.metadata?.vestingId && (
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">Contract Vesting ID</Typography>
            <Typography variant="body1">
              <Chip label={record.metadata.vestingId} size="small" color="primary" />
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const VestingPlanList = () => (
  <List actions={<VestingPlanListActions />} loading={true}>
    <Datagrid>
      <TextField source="id" label="ID" />
      <TextField source="name" label="Name" />
      <TextField source="description" label="Description" />
      <NumberField source="cliffDuration" label="Cliff (months)" />
      <NumberField source="totalPeriods" label="Total Periods" />
      <NumberField source="tgePercent" label="TGE %" />
      <BooleanField source="active" label="Active" />
      <DateField source="createdAt" label="Created" showTime />
      <ShowButton />
      <DeleteButton />
    </Datagrid>
  </List>
);

// Assigned Users Component
const AssignedUsersList = ({ vestingId }: { vestingId: string }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();

  React.useEffect(() => {
    loadAssignedUsers();
  }, [vestingId]);

  const loadAssignedUsers = async () => {
    setLoading(true);
    try {
      const result = await dataProvider.getList('users', {
        filter: { 
          isAssignedToVesting: true,
          assignedVestingId: vestingId 
        },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'assignedAt', order: 'DESC' }
      });
      setUsers(result.data);
    } catch (error) {
      console.error('Failed to load assigned users:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          👥 Assigned Users ({users.length})
        </Typography>
        {users.length === 0 ? (
          <Alert severity="info">No users assigned to this vesting plan</Alert>
        ) : (
          <MuiList>
            {users.map((user) => (
              <ListItem key={user.address} dense>
                <ListItemText
                  primary={user.address}
                  secondary={`${(Number(user.totalAllocation) / 1e18).toFixed(2)} ATLA - Vesting ID: ${user.vestingId}`}
                />
              </ListItem>
            ))}
          </MuiList>
        )}
      </CardContent>
    </Card>
  );
};

export const VestingPlanShow = () => {
  return (
    <Show actions={<VestingPlanShowActions />}>
      <SimpleShowLayout>
        <FunctionField render={(record: any) => <VestingPlanDetails record={record} />} />
        
        
        <Box sx={{ mt: 2 }}>
          <FunctionField 
            render={(record: any) => <AssignedUsersList vestingId={record.id} />} 
          />
        </Box>
        
        <TextField source="name" label="Name" />
        <TextField source="description" label="Description" />
        <NumberField source="cliffDuration" label="Cliff Duration (months)" />
        <NumberField source="totalPeriods" label="Total Periods" />
        <NumberField source="tgePercent" label="TGE Percentage (basis points)" />
        <NumberField source="unlockPercentPerPeriod" label="Unlock per Period (basis points)" />
        <BooleanField source="active" label="Active" />
        <DateField source="createdAt" label="Created At" showTime />
        <DateField source="updatedAt" label="Updated At" showTime />
      </SimpleShowLayout>
    </Show>
  );
};

// Editing disabled: vestings are immutable on-chain
export const VestingPlanEdit = () => null as any;

export const VestingPlanCreate = () => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  React.useEffect(() => {
    loadUnassignedUsers();
  }, []);

  const loadUnassignedUsers = async () => {
    setLoading(true);
    try {
      const result = await dataProvider.getList('users', {
        filter: { isAssignedToVesting: false },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'createdAt', order: 'DESC' }
      });
      setUsers(result.data);
    } catch (error) {
      notify('Failed to load users', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (address: string) => {
    setSelectedUsers(prev => 
      prev.includes(address) 
        ? prev.filter(addr => addr !== address)
        : [...prev, address]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.address));
    }
  };

  return (
    <Create 
      transform={(data: any) => {
        const cliffDuration = data.cliffDuration;
        const totalPeriods = data.totalPeriods;
        const tgePercent = data.tgePercent;
        
        const remaining = Math.max(10000 - Math.min(Math.max(Number(tgePercent) || 0, 0), 10000), 0);
        const unlockPercentPerPeriod = (Number(totalPeriods) || 0) > 0 ? Math.floor(remaining / Number(totalPeriods)) : 0;
        
        console.log('Creating vesting plan with:', {
          cliffDuration,
          totalPeriods,
          tgePercent,
          unlockPercentPerPeriod,
          userAddresses: selectedUsers
        });
        
        return {
          name: data.name,
          description: data.description,
          cliffDuration,
          totalPeriods,
          durationPeriodInMonths: 1,
          tgePercent,
          unlockPercentPerPeriod,
          userAddresses: selectedUsers,
        };
      }}
      redirect="show"
    >
      <SimpleForm>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>📝 Basic Information</Typography>
        <TextInput source="name" validate={[required()]} fullWidth />
        <TextInput source="description" validate={[required()]} fullWidth multiline rows={3} />
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>📅 Vesting Parameters</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <NumberInput 
              source="cliffDuration" 
              label="Cliff Duration (months)" 
              validate={[required()]}
              fullWidth
              helperText="Lock period before vesting starts"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <NumberInput 
              source="totalPeriods" 
              label="Total Periods" 
              validate={[required()]}
              fullWidth
              helperText="Number of vesting periods"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <NumberInput 
              source="tgePercent" 
              label="TGE Percentage (basis points)" 
              validate={[required()]}
              fullWidth
              helperText="Percentage unlocked at TGE (1000 = 10%)"
            />
          </Grid>
        </Grid>

        {/* User Selection Section */}
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              👥 Select Users for Vesting Plan
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose users to include in this vesting plan. Merkle tree will be built automatically.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAll}
              >
                {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Chip 
                label={`${selectedUsers.length} users selected`} 
                color={selectedUsers.length > 0 ? 'primary' : 'default'}
              />
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                <MuiList>
                  {users.map((user) => (
                    <ListItem key={user.address} dense>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedUsers.includes(user.address)}
                          onChange={() => handleUserToggle(user.address)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={user.address}
                        secondary={`${(Number(user.totalAllocation) / 1e18).toFixed(2)} ATLA - Vesting ID: ${user.vestingId}`}
                      />
                    </ListItem>
                  ))}
                  {users.length === 0 && (
                    <Alert severity="info">No unassigned users found</Alert>
                  )}
                </MuiList>
              </Box>
            )}
          </CardContent>
        </Card>
      </SimpleForm>
    </Create>
  );
};
